import { useCallback, useMemo } from 'react';
import type { UseWidgetModeResult, WidgetModeParams } from '../types';
import { useAuthorizedTradingUrl } from '../api/mutations/useAuthorizedTradingUrl';
import { useSessionStore } from '@/entities/session';

export function useWidgetMode(): UseWidgetModeResult {
  const session = useSessionStore();
  const { isWidget, params } = useMemo(() => {
    const savedParams = sessionStorage.getItem('widgetParams');
    const searchParams = new URLSearchParams(
      savedParams != null ? savedParams : window.location.search
    );

    const buildParams = (params: URLSearchParams): WidgetModeParams => ({
      state: params.get('state') ?? '',
      response_type: params.get('response_type') ?? '',
      embedded: params.get('embedded') === 'true',
      client_id: params.get('client_id') ?? '',
      theme: params.get('theme') ?? '',
      redirect_uri: params.get('redirect_uri') ?? '',
    });

    if (searchParams.get('embedded') === 'true') {
      if (savedParams === null) {
        sessionStorage.setItem('widgetParams', window.location.search);
      }
      return {
        isWidget: true,
        params: buildParams(searchParams),
      };
    }

    return {
      isWidget: false,
      params: undefined,
    };
  }, []);

  const { getUrl } = useAuthorizedTradingUrl();

  function clearWidgetModeParams() {
    sessionStorage.removeItem('widgetParams');
  }

  const anchorProps = useMemo(() => {
    return {
      target: isWidget ? '_blank' : undefined,
      rel: isWidget ? 'noopener noreferrer' : undefined,
    };
  }, [isWidget]);

  const handleTradingUrlNavigate = useCallback(async () => {
    if (session && params) {
      const { tradingUrl } = await getUrl({
        clientId: params.client_id,
        state: params.state,
        redirectUrl: params.redirect_uri,
        isReplace: true,
      });
      window.location.replace(tradingUrl);
    }
  }, [session && params]);

  return {
    isWidget,
    params,
    clearWidgetModeParams,
    anchorProps,
    handleTradingUrlNavigate,
  };
}
